import React, { useEffect, useRef } from 'react';
import lottie, { AnimationItem, RendererType } from 'lottie-web/build/player/lottie_light_canvas';

interface LottieOptions {
  animationData: unknown;
  loop?: boolean;
  autoplay?: boolean;
  renderer?: RendererType;
  rendererSettings?: Record<string, unknown>;
}

interface LottieWrapperProps {
  options: LottieOptions;
  style?: React.CSSProperties;
  isClickToPauseDisabled?: boolean;
  className?: string;
}

const LottieWrapper: React.FC<LottieWrapperProps> = ({
  options,
  style,
  isClickToPauseDisabled = false,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationItem | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    if (!containerRef.current) return;

    const { animationData, loop, autoplay, renderer, rendererSettings } = optionsRef.current;
    const anim = lottie.loadAnimation({
      container: containerRef.current,
      // lottie_light_canvas only includes the canvas renderer, so that's the only safe default.
      renderer: renderer ?? 'canvas',
      loop: loop !== false,
      autoplay: autoplay !== false,
      animationData,
      rendererSettings,
      // Use type assertion since rendererSettings' shape depends on the renderer
    } as Parameters<typeof lottie.loadAnimation>[0]);

    animationRef.current = anim;

    return () => {
      anim.destroy();
      animationRef.current = null;
    };
    // Only recreate the animation when the data itself changes; other options
    // (style, renderer settings) are new object references every render and
    // shouldn't restart playback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.animationData]);

  const togglePause = () => animationRef.current?.togglePause();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    togglePause();
  };

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        margin: '0 auto',
        outline: 'none',
        ...style,
      }}
      // Only present as an interactive button when clicking actually does something —
      // isClickToPauseDisabled is true at every current call site.
      onClick={isClickToPauseDisabled ? undefined : togglePause}
      onKeyDown={isClickToPauseDisabled ? undefined : handleKeyDown}
      role={isClickToPauseDisabled ? undefined : 'button'}
      aria-label={isClickToPauseDisabled ? undefined : 'animation'}
      tabIndex={isClickToPauseDisabled ? undefined : 0}
    />
  );
};

export default LottieWrapper;
